import { useMemo } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearRangePicker } from './shared/YearRangePicker'
import { YlyStatsGrid } from './shared/YlyStatsGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useYearlyData } from '../../hooks/useYearlyData'
import { useYearlyNormals } from '../../hooks/useYearlyNormals'
import { useYearRangeStore } from '../../store/useYearRangeStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'

const SLUG = 'yly-srazky'

const ROW_GROUPS = [
  {
    label: 'Srážky',
    rows: [
      { tsId: 'PRECIPSUM_SUM',  label: 'Roční úhrn srážek',            unit: 'mm',  normalMode: 'pct',  decimals: 1 },
      { tsId: 'PRECIPSUM_MAX',  label: 'Denní maximum srážek',          unit: 'mm',  normalMode: null,   decimals: 1 },
      { tsId: 'PRECIPSUM_G0',   label: 'Dny se srážkami ≥ 0.1 mm',     unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE10', label: 'Dny se srážkami ≥ 10 mm',      unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE50', label: 'Dny se srážkami ≥ 50 mm',      unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Sluneční svit & záření',
    rows: [
      { tsId: 'SUNSHINESUM_SUM',  label: 'Délka slunečního svitu', unit: 'hod',    normalMode: 'pct',  decimals: 1 },
      { tsId: 'GLOBALRADSUM_SUM', label: 'Globální záření',         unit: 'kJ/m²', normalMode: 'pct',  decimals: 0 },
      { tsId: 'CLOUDAVG_AVG',     label: 'Průměrná oblačnost',      unit: 'okty',  normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Sněhová pokrývka',
    rows: [
      { tsId: 'SNOW_MAX',    label: 'Maximum sněhové pokrývky',     unit: 'cm',  normalMode: null,   decimals: 0 },
      { tsId: 'SNOW_GE1',    label: 'Dny se sněhem ≥ 1 cm',         unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_GE10',   label: 'Dny se sněhem ≥ 10 cm',        unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOWNEW_MAX', label: 'Maximum nového sněhu',          unit: 'cm',  normalMode: null,   decimals: 0 },
      { tsId: 'SNOWNEW_GE1', label: 'Dny s novým sněhem ≥ 1 cm',    unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka.
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  PRECIPSUM_SUM: 'Roční úhrn', PRECIPSUM_MAX: 'Denní max.',
  PRECIPSUM_G0: '≥ 0.1 mm', PRECIPSUM_GE10: '≥ 10 mm', PRECIPSUM_GE50: '≥ 50 mm',
  SUNSHINESUM_SUM: 'Délka svitu', GLOBALRADSUM_SUM: 'Glob. záření', CLOUDAVG_AVG: 'Oblačnost',
  SNOW_MAX: 'Max. výška', SNOW_GE1: '≥ 1 cm', SNOW_GE10: '≥ 10 cm',
  SNOWNEW_MAX: 'Max. nový', SNOWNEW_GE1: 'nový ≥ 1 cm',
}, {
  'Sluneční svit & záření': 'Svit & záření',
  'Sněhová pokrývka': 'Sníh',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function YlySrazkyView({ station }) {
  const { yearFrom, yearTo, setYearFrom, setYearTo } = useYearRangeStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const years = useMemo(
    () => Array.from({ length: yearTo - yearFrom + 1 }, (_, i) => String(yearFrom + i)),
    [yearFrom, yearTo]
  )

  const { dataMap, isLoading: dLoading, isError: dError } = useYearlyData(station?.gh_id, yearFrom, yearTo, TS_IDS)
  const { normalsMap, isLoading: nLoading }               = useYearlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearRangePicker yearFrom={yearFrom} yearTo={yearTo} onYearFromChange={setYearFrom} onYearToChange={setYearTo} />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <YlyStatsGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            normalsMap={normalsMap}
            years={years}
          />
        )}
      </div>
    </div>
  )
}
